/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React, { useCallback, useEffect, useState } from 'react'
import { View, Text, Icon } from '@components/atoms'
import { Select, MenuItem, Button, IconButton, SelectChangeEvent } from '@mui/material'
import { mono, boShared } from '@components/systems/backoffice/backoffice.tokens'
import dayjs from 'dayjs'

import { InquiryType } from '@schemas'
import {
	INQUIRY_STATUS_OPTIONS,
	INQUIRY_STATUS_LABEL,
	INQUIRY_CATEG_LABEL,
} from '@configs'

interface PropsType {
	inquiry: InquiryType
	userList: { id: string; name: string }[]
	/** 드로어가 이 문의를 보고 있다 */
	isSelected: boolean
	/** 본문 전체·첨부·번역이 펼쳐져 있다 */
	isExpanded: boolean
	/** 페이지가 들고 있는 번역 결과 (카드가 화면 밖으로 나가도 남는다) */
	translated: string
	/** 카드 본체 클릭 — 펼치고, 드로어가 열려 있으면 이 카드로 바꾼다 */
	onCardClick: (inquiryId: string) => void
	/** 펼치기/접기만 (셰브론) */
	onToggleExpand: (inquiryId: string) => void
	onOpenTimeline: (inquiryId: string) => void
	onReply: (inquiryId: string) => void
	onStatusChange: (inquiryId: string, status: string) => void
	onHandlerChange: (inquiryId: string, handlerId: string) => void
	onDelete: (inquiryId: string) => void
	onTranslated: (inquiryId: string, text: string) => void
	/** 높이가 바뀐 뒤 가상 리스트에 다시 재도록 알린다 */
	onMeasure: () => void
}

/** 상태는 색이 아니라 칩의 채움 단계로 갈린다 — 신규 solid · 처리중 outline · 대기 기본 · 해결/종료 faint */
export const INQUIRY_STATUS_CHIP: Record<string, string> = {
	new: 'solid',
	in_progress: 'outline',
	pending: '',
	resolved: 'faint',
	closed: 'faint',
}

const stop = (e: React.SyntheticEvent) => e.stopPropagation()

const InquiryCardComponent = (props: PropsType) => {
	const {
		inquiry,
		userList,
		isSelected,
		isExpanded,
		translated,
		onCardClick,
		onToggleExpand,
		onOpenTimeline,
		onReply,
		onStatusChange,
		onHandlerChange,
		onDelete,
		onTranslated,
		onMeasure,
	} = props

	const [isTranslating, setIsTranslating] = useState(false)
	const [translateError, setTranslateError] = useState('')

	const images = inquiry.images ?? []

	// 펼침·번역·오류 문구가 바뀌면 높이가 바뀐다
	useEffect(() => {
		onMeasure()
	}, [isExpanded, translated, translateError, images.length, onMeasure])

	const onTranslate = useCallback(async () => {
		if (!inquiry.message || isTranslating) return
		setIsTranslating(true)
		setTranslateError('')
		try {
			const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=ko&dt=t&q=${encodeURIComponent(
				inquiry.message,
			)}`
			const res = await fetch(url)
			if (!res.ok) throw new Error('translation failed')
			const data = await res.json()
			const text = (data?.[0] ?? []).map((segment: any) => segment?.[0] ?? '').join('')
			onTranslated(inquiry.id, text)
		} catch (err) {
			setTranslateError('번역에 실패했습니다.')
		} finally {
			setIsTranslating(false)
		}
	}, [inquiry.id, inquiry.message, isTranslating, onTranslated])

	return (
		<View
			css={style}
			className={`inquiryCard ${isSelected ? 'selected' : ''} ${
				isExpanded ? 'expanded' : ''
			}`}
			onClick={() => onCardClick(inquiry.id)}
		>
			<View className="cardHead">
				<View className="controlGroup" onClick={stop}>
					<Select
						value={inquiry.status || ''}
						onChange={(e: SelectChangeEvent<string>) =>
							onStatusChange(inquiry.id, e.target.value)
						}
						size="small"
						displayEmpty
						variant="standard"
						disableUnderline
						className="statusSelect"
						renderValue={value =>
							value ? (
								<span className={`boChip ${INQUIRY_STATUS_CHIP[value] ?? ''} statusChip`}>
									{INQUIRY_STATUS_LABEL[value] ?? value}
								</span>
							) : (
								<span className="boChip faint statusChip">미설정</span>
							)
						}
					>
						{INQUIRY_STATUS_OPTIONS.map(opt => (
							<MenuItem key={opt.value} value={opt.value}>
								{opt.label}
							</MenuItem>
						))}
					</Select>
				</View>

				<View className="identity">
					<Text category="s1" className="name">
						{inquiry.name || '-'}
					</Text>
					<Text category="p2" className="email">
						{inquiry.email || '-'}
					</Text>
				</View>

				<span className="boChip categChip">
					{inquiry.categ ? INQUIRY_CATEG_LABEL[inquiry.categ] ?? inquiry.categ : '-'}
				</span>

				{/* 마지막 공개 코멘트가 고객 댓글 — 스태프가 아직 답하지 않았다 (INQUIRY_THREAD_PLAN §7) */}
				{inquiry.lastPublicKind === 'customer' && (
					<span className="boChip solid customerReplyChip">고객 답글</span>
				)}

				<View className="headSpacer" />

				<Text category="c1" className="date">
					{inquiry.createdAt ? dayjs(inquiry.createdAt).format('YYYY-MM-DD HH:mm') : '-'}
				</Text>
			</View>

			<View className="cardBody">
				<Text category="p1" className={`message ${isExpanded ? '' : 'clamped'}`}>
					{inquiry.message || '-'}
				</Text>

				{isExpanded && (
					<View className="expandedArea" onClick={stop}>
						{(inquiry.phone || inquiry.nationality) && (
							<View className="metaRow">
								{inquiry.phone && (
									<Text
										category="c1"
										className="metaText"
									>{`전화 ${inquiry.phone}`}</Text>
								)}
								{inquiry.nationality && (
									<Text
										category="c1"
										className="metaText"
									>{`국적 ${inquiry.nationality}`}</Text>
								)}
							</View>
						)}

						{images.length > 0 && (
							<View className="imageList">
								{images.map((anImageUrl, index) => (
									<a
										key={anImageUrl}
										className="imageThumb"
										href={anImageUrl}
										target="_blank"
										rel="noopener noreferrer"
									>
										<img
											src={anImageUrl}
											alt={`첨부 이미지 ${index + 1}`}
											loading="lazy"
										/>
									</a>
								))}
							</View>
						)}

						{translated ? (
							<View className="translatedBox">
								<span className="boMeta translatedLabel">한글 번역</span>
								<Text category="p1" className="message">
									{translated}
								</Text>
							</View>
						) : (
							<View className="translateActions">
								<Button
									size="small"
									variant="outlined"
									onClick={() => {
										void onTranslate()
									}}
									disabled={!inquiry.message || isTranslating}
									startIcon={
										<Icon name="translate" style={{ fontSize: 16, color: 'inherit' }} />
									}
								>
									{isTranslating ? '번역 중...' : '한글로 번역'}
								</Button>
								{translateError && <span className="boError">{translateError}</span>}
							</View>
						)}
					</View>
				)}
			</View>

			<View className="cardFoot" onClick={stop}>
				<Select
					value={inquiry.handlerId || ''}
					onChange={(e: SelectChangeEvent<string>) =>
						onHandlerChange(inquiry.id, e.target.value)
					}
					size="small"
					displayEmpty
					className="handlerSelect"
					startAdornment={
						<Icon name="account-outline" style={{ fontSize: 16, marginRight: 4 }} />
					}
					renderValue={value => {
						const name = userList.find(u => u.id === value)?.name
						return name ?? '미배정'
					}}
				>
					<MenuItem value="">
						<em>미배정</em>
					</MenuItem>
					{userList.map(u => (
						<MenuItem key={u.id} value={u.id}>
							{u.name}
						</MenuItem>
					))}
				</Select>

				{images.length > 0 && (
					<Text category="c1" className="attachCount">
						{`첨부 ${images.length}`}
					</Text>
				)}

				<View className="footSpacer" />

				<Button
					size="small"
					variant="text"
					onClick={() => onReply(inquiry.id)}
					disabled={!inquiry.email}
					startIcon={
						<Icon name="email-outline" style={{ fontSize: 16, color: 'inherit' }} />
					}
				>
					이메일 답변
				</Button>
				<Button
					size="small"
					variant={isSelected ? 'contained' : 'outlined'}
					onClick={() => onOpenTimeline(inquiry.id)}
					startIcon={
						<Icon
							name="timeline-text-outline"
							style={{ fontSize: 16, color: 'inherit' }}
						/>
					}
				>
					타임라인
				</Button>
				<IconButton size="small" onClick={() => onDelete(inquiry.id)} title="문의 삭제">
					<Icon name="trash-can-outline" style={{ fontSize: 18, color: 'inherit' }} />
				</IconButton>
				<IconButton
					size="small"
					onClick={() => onToggleExpand(inquiry.id)}
					title={isExpanded ? '접기' : '펼치기'}
				>
					<Icon
						name={isExpanded ? 'chevron-up' : 'chevron-down'}
						style={{ fontSize: 20, color: 'inherit' }}
					/>
				</IconButton>
			</View>
		</View>
	)
}

/** props 가 같으면 다시 그리지 않는다 — 카드가 많아도 한 장 바뀔 때 한 장만 그린다 */
export const InquiryCard = React.memo(InquiryCardComponent)

const style = css({
	...boShared,
	background: mono.surface,
	border: `1px solid ${mono.line}`,
	borderLeft: `1px solid ${mono.line}`,
	borderRadius: 0,
	padding: '14px 18px',
	cursor: 'pointer',
	transition: 'border-color 200ms ease-out, background-color 200ms ease-out',

	'&:hover': {
		borderColor: mono.lineSoft,
	},
	'&.selected': {
		borderColor: mono.ink,
		borderLeft: `2px solid ${mono.ink}`,
	},

	'.cardHead': {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
		minWidth: 0,
	},
	'.controlGroup': {
		flexShrink: 0,
		cursor: 'default',
	},
	'.statusSelect': {
		'.MuiSelect-select': {
			padding: '0 !important',
			paddingRight: '0 !important',
			display: 'flex',
			alignItems: 'center',
		},
		'.MuiSelect-icon': { display: 'none' },
	},
	'.statusChip': { cursor: 'pointer' },
	'.identity': {
		display: 'flex',
		alignItems: 'baseline',
		gap: 8,
		minWidth: 0,
		flexShrink: 1,
		overflow: 'hidden',
		'.name': { whiteSpace: 'nowrap', fontWeight: 500, color: mono.ink },
		'.email': {
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
			fontWeight: 300,
			color: mono.dim,
		},
	},
	'.categChip': { flexShrink: 0 },
	'.customerReplyChip': { flexShrink: 0 },
	'.headSpacer': { flex: 1 },
	'.date': { whiteSpace: 'nowrap', flexShrink: 0, color: mono.faint, fontWeight: 300 },

	'.cardBody': {
		marginTop: 10,
	},
	'.message': {
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
		fontWeight: 300,
		color: mono.ink,
		lineHeight: '1.55 !important',
		'&.clamped': {
			display: '-webkit-box',
			WebkitLineClamp: 3,
			WebkitBoxOrient: 'vertical',
			overflow: 'hidden',
		},
	},
	'.expandedArea': {
		cursor: 'default',
		marginTop: 10,
		display: 'flex',
		flexDirection: 'column',
		gap: 10,
	},
	'.metaRow': {
		display: 'flex',
		gap: 16,
		'.metaText': { color: mono.dim },
	},
	'.imageList': {
		display: 'flex',
		flexWrap: 'wrap',
		gap: 8,
	},
	'.imageThumb': {
		display: 'block',
		width: '7rem',
		height: '7rem',
		borderRadius: 0,
		border: `1px solid ${mono.line}`,
		overflow: 'hidden',
		background: mono.bg,
		img: {
			width: '100%',
			height: '100%',
			objectFit: 'cover',
			display: 'block',
		},
	},
	'.translateActions': {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
	},
	'.translatedBox': {
		padding: '12px 0 0',
		borderTop: `1px solid ${mono.line}`,
	},
	'.translatedLabel': {
		display: 'block',
		marginBottom: 6,
	},

	'.cardFoot': {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		marginTop: 10,
		paddingTop: 10,
		borderTop: `1px solid ${mono.line}`,
		cursor: 'default',
	},
	'.handlerSelect': {
		height: 30,
		fontSize: 13,
		minWidth: '9rem',
		'.MuiSelect-select': { paddingTop: 4, paddingBottom: 4 },
	},
	'.attachCount': { whiteSpace: 'nowrap', color: mono.dim },
	'.footSpacer': { flex: 1 },

	'@media (max-width: 720px)': {
		'.cardHead': { flexWrap: 'wrap' },
		'.date': { width: '100%' },
	},
})
